import { Button, Center, Container, Group, Loader, Paper, Stack, Text, Title } from '@mantine/core';
import { IconHome, IconX } from '@tabler/icons-react';
import { type ReactNode, useEffect, useEffectEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { notifyError } from '@/shared/notify';

type GuestLobbyState = 'none' | 'waiting_employee' | 'waiting' | 'admitted' | 'rejected';
type GuestLobbyResponse = { state: GuestLobbyState };

const POLL_INTERVAL_MS = 2000;

type GuestLobbyProps = {
    roomName: string;
    onAdmitted: () => void;
    onCancel: () => void;
    onGuestsClosed: () => void;
};

export function GuestLobby({ roomName, onAdmitted, onCancel, onGuestsClosed }: Readonly<GuestLobbyProps>) {
    const { t } = useTranslation('prejoin');
    const [state, setState] = useState<GuestLobbyState>('none');
    const [cancelling, setCancelling] = useState(false);
    const lobbyUrl = `/api/meetings/${encodeURIComponent(roomName)}/lobby`;
    const handleAdmitted = useEffectEvent(onAdmitted);
    const handleGuestsClosed = useEffectEvent(onGuestsClosed);

    useEffect(() => {
        const controller = new AbortController();
        let failed = false;
        let inFlight = false;
        let method: 'POST' | 'GET' = 'POST';

        const poll = async () => {
            if (inFlight) return;
            inFlight = true;
            let next: GuestLobbyState | undefined;
            try {
                const response = await fetch(method === 'POST' ? lobbyUrl : `${lobbyUrl}/me`, {
                    method,
                    signal: controller.signal,
                });
                if (response.status === 404) {
                    clearInterval(timer);
                    handleGuestsClosed();
                    return;
                }
                if (!response.ok) throw new Error(await response.text());
                const body = (await response.json()) as GuestLobbyResponse;
                next = body.state;
                failed = false;
            } catch (error) {
                if (!controller.signal.aborted && !failed) notifyError(t('lobby.error'), error);
                failed = true;
            } finally {
                inFlight = false;
            }
            if (controller.signal.aborted || !next) return;
            setState(next);
            method = next === 'none' ? 'POST' : 'GET';
            if (next === 'admitted' || next === 'rejected') clearInterval(timer);
            if (next === 'admitted') handleAdmitted();
        };

        void poll();
        const timer = setInterval(() => void poll(), POLL_INTERVAL_MS);
        return () => {
            controller.abort();
            clearInterval(timer);
        };
    }, [lobbyUrl, t]);

    const cancel = async () => {
        setCancelling(true);
        try {
            await fetch(`${lobbyUrl}/me`, { method: 'DELETE' });
        } catch (error) {
            console.error(error);
        }
        onCancel();
    };

    if (state === 'rejected') {
        return (
            <LobbyCard roomName={roomName} title={t('lobby.rejectedTitle')}>
                <Text>{t('lobby.rejectedDescription')}</Text>
                <Button component={Link} leftSection={<IconHome size={20} />} to="/" variant="light">
                    {t('lobby.home')}
                </Button>
            </LobbyCard>
        );
    }

    const waitingForEmployee = state === 'waiting_employee';
    return (
        <LobbyCard roomName={roomName} title={waitingForEmployee ? t('lobby.noEmployeeTitle') : t('lobby.waitingTitle')}>
            <Group gap="var(--spacing-3)" wrap="nowrap">
                <Loader size="sm" />
                <Text>{waitingForEmployee ? t('lobby.noEmployeeDescription') : t('lobby.waitingDescription')}</Text>
            </Group>
            <Button leftSection={<IconX size={20} />} loading={cancelling} variant="default" onClick={() => void cancel()}>
                {t('lobby.cancel')}
            </Button>
        </LobbyCard>
    );
}

function LobbyCard({ roomName, title, children }: Readonly<{ roomName: string; title: string; children: ReactNode }>) {
    return (
        <Center mih="100dvh" p="md">
            <Container p={0} size="xs" w="100%">
                <Paper withBorder p="xl">
                    <Stack align="flex-start" gap="md">
                        <Title order={2}>{title}</Title>
                        <Text c="dimmed">{roomName}</Text>
                        {children}
                    </Stack>
                </Paper>
            </Container>
        </Center>
    );
}
